import { useEffect, useRef } from 'react';

export const IDLE_TIMEOUT_MS = 30 * 60 * 1000;

export const ACTIVITY_STORAGE_KEY = 'last_activity_at';
const ACTIVITY_THROTTLE_MS = 5 * 1000;
const CHECK_INTERVAL_MS = 30 * 1000;
const ACTIVITY_EVENTS = ['mousedown', 'keydown', 'touchstart', 'scroll', 'mousemove'];

function readLastActivity() {
  const raw = localStorage.getItem(ACTIVITY_STORAGE_KEY);
  const parsed = raw ? Number(raw) : NaN;
  return Number.isFinite(parsed) ? parsed : null;
}

/**
 * Logs the user out after `timeoutMs` of inactivity.
 *
 * Activity is shared across tabs via localStorage so a user typing in one tab
 * keeps every other tab alive. Once the threshold elapses, `onTimeout` fires
 * exactly once per mount.
 */
export function useIdleTimeout(onTimeout, timeoutMs = IDLE_TIMEOUT_MS) {
  const onTimeoutRef = useRef(onTimeout);
  onTimeoutRef.current = onTimeout;

  useEffect(() => {
    let firedRef = false;
    let lastWriteAt = 0;

    const recordActivity = () => {
      if (firedRef) return;
      const now = Date.now();
      if (now - lastWriteAt < ACTIVITY_THROTTLE_MS) return;
      lastWriteAt = now;
      localStorage.setItem(ACTIVITY_STORAGE_KEY, String(now));
    };

    const checkIdle = () => {
      if (firedRef) return;
      const lastActivity = readLastActivity() ?? Date.now();
      if (Date.now() - lastActivity >= timeoutMs) {
        firedRef = true;
        onTimeoutRef.current();
      }
    };

    const handleStorage = (event) => {
      if (event.key === ACTIVITY_STORAGE_KEY) {
        lastWriteAt = Date.now();
      }
    };

    // Seed the timestamp so the first check after mount doesn't trip on a
    // stale value from a prior session.
    localStorage.setItem(ACTIVITY_STORAGE_KEY, String(Date.now()));
    lastWriteAt = Date.now();

    ACTIVITY_EVENTS.forEach((evt) => {
      window.addEventListener(evt, recordActivity, { passive: true });
    });
    window.addEventListener('storage', handleStorage);

    const intervalId = setInterval(checkIdle, CHECK_INTERVAL_MS);

    return () => {
      ACTIVITY_EVENTS.forEach((evt) => {
        window.removeEventListener(evt, recordActivity);
      });
      window.removeEventListener('storage', handleStorage);
      clearInterval(intervalId);
    };
  }, [timeoutMs]);
}
